import React from 'react'
import styled from '@emotion/styled'
import { NumericInput, Field } from 'view/components/inputs'
import { primitive } from 'utils/index'
import * as constants from 'view/styles/constants'

export interface DurationInputProps {
  id?: string
  className?: string
  value: number
  readOnly?: boolean
  disabled?: boolean
  onChange?: () => void
  onUpdate: (value: primitive | undefined | null) => void
  dataTestId?: string
}

interface DurationInputState {
  isFocused: boolean
  currentValue: number
  hours: number
  minutes: number
}

export const InputWrapper = styled.div`
  border-radius: 6px;
  height: 24px;
  display: flex;
  align-items: center;
  outline: 0;
  background-color: ${constants.CORE_PRIMARY};
  width: 100%;
`

export const NumericInputHours = styled(NumericInput)`
  width: 28px;
  border: none;
  height: 20px;
  font-size: 12px;
  color: ${constants.CORE_PRIMARY_6};
`

export const NumericInputMinutes = styled(NumericInputHours)`
  text-align: left;
`

const Separator = styled.span`
  width: 4px;
  height: 22px;
  line-height: 20px;
  color: ${constants.CORE_PRIMARY_6};
`

export class DurationInput extends React.Component<DurationInputProps, DurationInputState> {
  static getDerivedStateFromProps(nextProps: DurationInputProps, state: DurationInputState) {
    if (state.currentValue !== nextProps.value) {
      const hours = Math.floor(nextProps.value / 60)
      const mins = nextProps.value - hours * 60
      return {
        hours: hours,
        minutes: mins,
        currentValue: nextProps.value
      }
    }
    return null
  }

  constructor(props: DurationInputProps) {
    super(props)
    const hours = Math.floor(props.value / 60)
    const mins = props.value - hours * 60
    this.state = {
      isFocused: false,
      hours: hours,
      minutes: mins,
      currentValue: props.value
    }
  }

  private wrapperRef = React.createRef<HTMLDivElement>()

  render() {
    const { id, className } = this.props

    return (
      <InputWrapper
        data-test-id={this.props.dataTestId}
        className={className}
        ref={this.wrapperRef}
        tabIndex={-1}
        onFocus={this.handleWrapperFocus}
        onBlur={this.handleWrapperBlur}>
        <NumericInputHours
          id={id}
          min={0}
          max={23}
          maxLength={2}
          value={this.state.hours}
          readOnly={this.props.readOnly}
          disabled={this.props.disabled}
          formatOptions={{ minDigits: 2 }}
          onUpdate={this.handleUpdateHours}
          onChange={this.handleChange}
        />
        <Separator>:</Separator>
        <NumericInputMinutes
          min={0}
          max={59}
          maxLength={2}
          value={this.state.minutes}
          readOnly={this.props.readOnly}
          disabled={this.props.disabled}
          formatOptions={{ minDigits: 2 }}
          onUpdate={this.handleUpdateMinutes}
          onChange={this.handleChange}
        />
      </InputWrapper>
    )
  }

  private handleWrapperFocus = (_evt: React.FocusEvent) => {
    this.setState({ isFocused: true })
  }

  private handleWrapperBlur = (evt: React.FocusEvent) => {
    if (this.wrapperRef.current) {
      if (!this.wrapperRef.current.contains(evt.relatedTarget as any)) {
        this.setState({ isFocused: false }, this.triggerUpdate)
      }
    }
  }

  private handleUpdateHours = (val: number | null) => {
    if (val === null || isNaN(val) || val < 0) {
      this.setState({ hours: 0 }, this.triggerUpdate)
    } else {
      this.setState({ hours: val }, this.triggerUpdate)
    }
  }

  private handleUpdateMinutes = (val: number | null) => {
    if (val === null || isNaN(val) || val < 0) {
      this.setState({ minutes: 0 }, this.triggerUpdate)
    } else {
      this.setState({ minutes: val }, this.triggerUpdate)
    }
  }

  private handleChange = () => {
    if (this.props.onChange) {
      this.props.onChange()
    }
  }

  private triggerUpdate = () => {
    const { hours, minutes } = this.state
    const duration = hours * 60 + minutes
    this.props.onUpdate(duration)
  }
}

export const DurationInputField = Field(DurationInput)
